// Cleaning the text pdf.js, OCR, Word or the server's converter gives us before the rules read it:
// ligatures and full-width characters, words hyphenated across lines, page numbers and repeated
// headers, s p a c e d - o u t headings, and OCR mixing up O/0 and l/1 inside numbers.

export interface Doc {
  /** Cleaned lines, trimmed; blank lines kept as paragraph breaks. */
  lines: string[];
  /** The cleaned lines joined with "\n" (for labels, signature blocks and headings). */
  raw: string;
  /** All whitespace collapsed to single spaces (for sentences and clauses). */
  flat: string;
  /**
   * Where in `flat` a new sentence starts because of the layout, not punctuation: a list item, a
   * "Label: value" line, a heading, and the line after a label or heading. Schedules and lists
   * rarely end their lines with a full stop, so without these they read as one long sentence.
   */
  breaks: number[];
}

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u200b-\u200d\u2060\ufeff\u00ad]/g;
const SPACES = /[\u00a0\u1680\u2000-\u200a\u202f\u205f\u3000\t]/g;
const PAGE_FURNITURE = /^(?:page\s+\d+(?:\s*(?:of|\/)\s*\d+)?|\d+\s*(?:of|\/)\s*\d+|[-–—]\s*\d+\s*[-–—]|\d{1,3}|[_=.\-–—\s*]{4,})$/i;

/** "R E N T   A G R E E M E N T" → "RENT AGREEMENT": letters one space apart, words two or more apart. */
function unspace(line: string): string {
  if (!/(?:\b\p{L} ){3,}\p{L}\b/u.test(line)) return line;
  return line
    .split(/ {2,}/)
    .map((chunk) => (/^(?:\p{L} ){2,}\p{L}$/u.test(chunk) ? chunk.replace(/ /g, "") : chunk))
    .join(" ");
}

/** OCR reads 0 as O and 1 as l or I inside numbers: "2O26" → "2026", "0l.10.2026" → "01.10.2026". */
function fixOcrDigits(line: string): string {
  return line.replace(/(?<![\p{L}])[0-9OoIl]{2,}(?:[./-][0-9OoIl]{1,4}){0,2}(?![\p{L}])/gu, (token) => {
    const digits = token.replace(/[^0-9]/g, "").length;
    const letters = token.replace(/[^OoIl]/g, "").length;
    if (!letters || digits < 2 || letters > digits / 2) return token;
    return token.replace(/[Oo]/g, "0").replace(/[Il]/g, "1");
  });
}

export function normalize(input: string): Doc {
  let text = (input || "").slice(0, 500_000).normalize("NFKC").replace(/\r\n?/g, "\n").replace(SPACES, " ").replace(CONTROL, "");
  text = text.replace(/[‐‑]/g, "-").replace(/[‘’‛]/g, "'").replace(/[“”‟]/g, '"');
  // Words split across lines: "Septem-\nber" → "September", "AGREE-\nMENT" → "AGREEMENT" (the next
  // line carries on in the same case). Anchored on the hyphen: starting from the letters would rescan
  // a long run of them at every position.
  text = text.replace(/(?<=\p{L}{2})-[ \t]*\n[ \t]*(?=\p{Ll}{2})|(?<=\p{Lu}{2})-[ \t]*\n[ \t]*(?=\p{Lu}{2})/gu, "");

  // Runs of spaces stay inside lines: they separate table columns and spaced-out headings.
  let lines = text.split("\n").map((l) => fixOcrDigits(unspace(l.trim())));
  // Running headers and footers repeat on every page; real clauses don't.
  const counts = new Map<string, number>();
  for (const l of lines) if (l.length >= 8 && l.length <= 90) counts.set(l, (counts.get(l) ?? 0) + 1);
  lines = lines.filter((l) => !PAGE_FURNITURE.test(l) && !((counts.get(l) ?? 0) >= 3 && /\p{L}/u.test(l) && !/[:]/.test(l)));

  const raw = lines.join("\n");
  // Built a line at a time so the layout breaks can be placed in the flat text.
  let flat = "";
  const breaks: number[] = [];
  let prevStructural = false;
  for (const line of lines) {
    const l = line.replace(/\s+/g, " ").replace(/ ([,.;:)])/g, "$1").replace(/\( /g, "(");
    if (!l) continue;
    // A wrapped line can start "(5) years from …": a list item follows a finished line, or starts a capitalised clause.
    const listItem = LIST_ITEM.test(l) && (!flat || /[.;:!?)]$/.test(flat) || prevStructural || /^\S+\s+[\p{Lu}"(]/u.test(l));
    const structural = listItem || LABEL_LINE.test(l) || isHeading(l);
    if (flat && !/^[,.;:)]/.test(l) && !flat.endsWith("(")) flat += " ";
    if (flat && (structural || prevStructural)) breaks.push(flat.length);
    flat += l;
    prevStructural = LABEL_LINE.test(l) || isHeading(l);
  }
  return { lines, raw, flat, breaks };
}

// "a) …", "(iv) …", "2. TERM", "3.1 The …", "• …"
const LIST_ITEM = /^(?:\(?(?:[a-z]|[ivx]{1,4}|\d{1,2})\)|\d{1,2}(?:\.\d{1,2})*\.?|[-•*·–▪●])\s+(?!(?:days?|months?|years?|weeks?|hours?|%|per|lakhs?|crores?)\b)/i;
// "Loan Amount: Rs. 8,50,000"
const LABEL_LINE = /^[A-Za-z][A-Za-z0-9 ()./&',@%-]{0,48}?\s*:\s*\S/;

/** "TERMINATION", "OWNER    TENANT": a short line in capitals. */
function isHeading(line: string): boolean {
  if (line.length > 80) return false;
  const letters = line.replace(/[^\p{L}]/gu, "");
  return letters.length >= 3 && letters.replace(/[^\p{Lu}]/gu, "").length >= letters.length * 0.85 && !/[,;]$/.test(line);
}

export interface Sentence {
  text: string;
  index: number;
}

const ABBREV = /\b(?:no|nos|mr|mrs|ms|dr|sr|jr|st|sh|smt|shri|sri|co|inc|ltd|pvt|corp|llc|vs|viz|etc|e\.g|i\.e|approx|sq|ft|rs|cl|art|sec|para|govt|dept|reg|regd|u\.s|s\.a|p\.a|p\.m|a\.m|a\.k\.a|m\/s)$/i;

/** Splits flat text into sentences (and list items), keeping where each starts. `breaks` are layout breaks from `normalize`. */
export function sentences(flat: string, breaks: readonly number[] = []): Sentence[] {
  const out: Sentence[] = [];
  let start = 0;
  let next = 0;
  const push = (end: number) => {
    const t = flat.slice(start, end).trim();
    if (t) out.push({ text: t, index: start + (flat.slice(start, end).length - flat.slice(start, end).trimStart().length) });
  };
  for (let i = 0; i < flat.length; i++) {
    if (i === breaks[next]) {
      push(i);
      start = i;
      while (breaks[next] !== undefined && breaks[next]! <= i) next++;
    }
    const c = flat[i]!;
    if (c === ";" || ((c === "." || c === "!" || c === "?") && /^\s+["“(]?[\p{Lu}\d]/u.test(flat.slice(i + 1, i + 4)))) {
      if (c === ".") {
        const word = /[\w./]+$/.exec(flat.slice(Math.max(0, i - 12), i))?.[0] ?? "";
        // "Rs. 25,000", "No. 402", "J. Smith" and "1. TERM" don't end a sentence.
        if (ABBREV.test(word) || /^\p{Lu}$/u.test(word) || /^\d{1,2}$/.test(word)) continue;
      }
      push(i + 1);
      start = i + 1;
    }
  }
  push(flat.length);
  return out;
}

/** "Policy Number : 4128/HO/…" lines (two to a line in tables) as label → value pairs, in order. */
export function labelLines(lines: readonly string[]): { label: string; value: string; line: number }[] {
  const out: { label: string; value: string; line: number }[] = [];
  lines.forEach((line, n) => {
    for (const part of line.split(/ {3,}(?=[A-Z][A-Za-z ()./&'-]{1,40}:)/)) {
      const m = /^([A-Za-z][A-Za-z0-9 ()./&',@%-]{0,48}?)\s*:\s*(\S.*)$/.exec(part.trim().replace(/^[-•*·–]\s+/, ""));
      if (m && !/https?$/i.test(m[1]!)) out.push({ label: m[1]!.trim(), value: m[2]!.trim(), line: n });
    }
  });
  return out;
}

/** A short passage around a match, for the user to check where a value came from. */
export function evidence(text: string, index: number, length: number): string {
  const start = Math.max(0, index - 60);
  const end = Math.min(text.length, index + length + 40);
  return (start > 0 ? "…" : "") + text.slice(start, end).replace(/\s+/g, " ").trim() + (end < text.length ? "…" : "");
}
